import { faker } from "@faker-js/faker";
import type { Meta, StoryObj } from "@storybook/react";

import type { ListMockItemProps } from "@/__mocks__/lists";
import { fakeSection } from "@/__mocks__/lists";
import { Text } from "@/ui/core";
import type { SectionListProps } from "@/ui/layout";
import { BaseCard, SectionList } from "@/ui/layout";
import type { ListSeparatorType } from "@/ui/layout/list/ListComponents";
import { ListDimensions } from "@/ui/layout/list/ListComponents";
import { joinTestIDs } from "@/utils/string.util";

const ListSeparatorValues: Record<ListSeparatorType, ListSeparatorType> = {
  line: "line",
  space: "space",
};

const TestID = {
  Card: joinTestIDs("storybook", "item-card"),
  Item: joinTestIDs("storybook", "item-text"),
  List: "storybook",
};

const SectionListMockValues = faker.helpers.multiple(() => fakeSection(), {
  count: 2,
});

const StoryMeta: Meta<SectionListProps<ListMockItemProps>> = {
  title: "Layout/Section List",
  component: SectionList,
  argTypes: {
    contentContainerPaddingBottom: { control: "number" },
    isDataLoading: { control: "boolean" },
    isHorizontal: { control: "boolean" },
    isRefreshControlLoading: { control: "boolean" },
    onRefresh: { action: "refreshed" },
    sectionSeparatorSize: { control: "select", options: ["md", "sm"] },
    separatorType: {
      control: "select",
      options: Object.values(ListSeparatorValues),
    },
    shouldShowRefreshControl: { control: "boolean" },
  },
  args: {
    contentContainerPaddingBottom: ListDimensions.FABBottomOffset,
    sections: SectionListMockValues,
    sectionSeparatorSize: "sm",
    separatorType: "space",
    testID: TestID.List,
  },
};

type Story = StoryObj<SectionListProps<ListMockItemProps>>;

const Primary: Story = {
  ...StoryMeta,
  render: (props) => (
    <SectionList
      {...props}
      emptyListComponent={() => (
        <Text
          testID={joinTestIDs("storybook", "empty-list")}
          variant="bodyMedium"
        >
          No items
        </Text>
      )}
      renderItem={({ index, item }) => (
        <BaseCard
          maxWidth={props.isHorizontal ? 200 : "unset"}
          testID={joinTestIDs(TestID.Card, `${index}`)}
        >
          <Text testID={joinTestIDs(TestID.Item, `${index}`)}>{item.text}</Text>
        </BaseCard>
      )}
      renderSectionHeader={({ section }) => (
        <Text testID={joinTestIDs("storybook", "section-header")} variant="h2">
          {section.title}
        </Text>
      )}
    />
  ),
};

const WithEmptyList: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    sections: [],
  },
};

const Horizontal: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    isHorizontal: true,
  },
};

const WithRefreshControl: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    isRefreshControlLoading: true,
    shouldShowRefreshControl: true,
  },
};

const WithLineSeparator: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    separatorType: "line",
  },
};

const WithHeaderAndFooter: Story = {
  ...Primary,
  args: {
    ...Primary.args,
    ListFooterComponent: () => (
      <Text testID="list-footer" variant="h2">
        Footer
      </Text>
    ),
    ListHeaderComponent: () => (
      <Text testID="list-header" variant="h2">
        Header
      </Text>
    ),
  },
};

export {
  Horizontal,
  Primary,
  WithEmptyList,
  WithHeaderAndFooter,
  WithLineSeparator,
  WithRefreshControl,
};
export default StoryMeta;
